import { Fragment, useLayoutEffect, useRef } from 'react';

import { Box, Divider } from '@mui/material';

import { MenuModulesItem } from 'components/header/menu-modules/MenuModulesItem';
import { MenuNode } from 'components/header/menu-modules/types';

export const TRANSITION_MS = 260;

export interface MenuModulesPanel {
    nodes: MenuNode[];
    title: string;
}

type MenuModulesNavigationStack = {
    stack: MenuModulesPanel[];
    currentIndex: number;
    onPush(node: MenuNode): void;
    onClose(): void;
    setActivePanelEl: (el: HTMLDivElement | null) => void;
};

export function MenuModulesNavigationStack({ stack, currentIndex, onPush, onClose, setActivePanelEl }: MenuModulesNavigationStack) {
    const scrollRefs = useRef<(HTMLDivElement | null)[]>([]);

    useLayoutEffect(() => {
        const el = scrollRefs.current[currentIndex];
        if (el) el.scrollTop = 0;
    }, [currentIndex]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                height: '100%',
                transform: `translateX(${-currentIndex * 100}%)`,
                transition: `transform ${TRANSITION_MS}ms cubic-bezier(0.4,0,0.2,1)`,
                willChange: 'transform',
            }}
        >
            {stack.map((panel, panelIdx) => {
                let currentGroup: number | undefined = undefined;
                const isActive = panelIdx === currentIndex;

                return (
                    <Box
                        key={panelIdx}
                        ref={(el: HTMLDivElement | null) => {
                            scrollRefs.current[panelIdx] = el;
                        }}
                        sx={{
                            width: '100%',
                            flexShrink: 0,
                            height: '100%',
                            overflowY: 'auto',
                        }}
                    >
                        <Box
                            ref={(el: HTMLDivElement | null) => {
                                if (isActive && el) setActivePanelEl(el);
                            }}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.75,
                            }}
                        >
                            {panel.nodes.map((node, nodeIndex) => {
                                const hasChildren = node.children && node.children.length > 0;
                                const showDivider = nodeIndex > 0 && node.group !== undefined && node.group !== currentGroup;

                                currentGroup = node.group;

                                return (
                                    <Fragment key={`${node.title}-${nodeIndex}`}>
                                        {showDivider && <Divider sx={{ mx: 0, my: 0.75 }} />}

                                        <MenuModulesItem node={node} onClose={hasChildren ? onPush : onClose} />
                                    </Fragment>
                                );
                            })}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
